/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "@/lib/msalInstance";
import { initLogRelay } from "@/lib/logRelay";
import { createLogRelaySender } from "@/lib/logRelaySender";
import App from "./App";
import "./index.css";

// Activate the browser→backend log relay in production only, so dev/test don't
// ship logs (and there's no backend to receive them). No-op until wired here.
if (import.meta.env.PROD) {
  initLogRelay(createLogRelaySender());
}

// Create a client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      // Refetch stale data when the user returns to the tab or the network reconnects. Combined
      // with the 30s staleTime this is a no-op while data is fresh, but it's the safety net that
      // keeps volatile screens current without a manual refresh (docs/specs/portal-data-freshness.md).
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");
createRoot(rootElement).render(
  <StrictMode>
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MsalProvider>
  </StrictMode>
);
